import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

type Choice = 'solo' | 'team';

/**
 * Онбординг при первом входе — «Ваша команда».
 * Показывается вместо основного приложения, пока у пользователя
 * needs_onboarding === true. После выбора App.tsx перерисовывает
 * основное приложение автоматически.
 */
export default function OnboardingPage() {
  const { user, completeOnboarding } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [choice, setChoice] = useState<Choice | null>(null);
  const [schoolName, setSchoolName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const firstName = user?.first_name?.trim() || 'друг';

  const handleSubmit = async () => {
    if (!choice) return;
    if (choice === 'team' && !schoolName.trim()) {
      setError('Введите название школы, чтобы продолжить');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await completeOnboarding(
        choice,
        choice === 'team' ? schoolName.trim() : undefined,
      );
      // Успех: needs_onboarding → false, App.tsx сам покажет приложение.
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Не удалось сохранить выбор. Попробуйте ещё раз.',
      );
      setSubmitting(false);
    }
  };

  const optionCard = (
    value: Choice,
    active: boolean,
    icon: string,
    title: string,
    description: string,
  ) => (
    <button
      type="button"
      onClick={() => {
        setChoice(value);
        setError(null);
      }}
      aria-pressed={active}
      className={`w-full text-left p-5 rounded-2xl border-2 transition-all duration-200 mb-3 active:scale-[0.98] ${
        active
          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 shadow-md'
          : 'border-transparent bg-[var(--tg-theme-section-bg-color)] hover:opacity-90'
      }`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 transition-all ${
            active
              ? 'bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)]'
              : 'bg-[var(--tg-theme-secondary-bg-color)]'
          }`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[var(--tg-theme-text-color)] text-sm mb-1">
            {title}
          </p>
          <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed">
            {description}
          </p>
        </div>
        {/* Radio indicator */}
        <span
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-1 transition-all ${
            active
              ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]'
              : 'border-[var(--tg-theme-hint-color)]/40'
          }`}
        >
          {active && <span className="text-[10px] text-[var(--tg-theme-button-text-color)]">✓</span>}
        </span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-[var(--tg-theme-secondary-bg-color)] flex flex-col safe-top">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="relative overflow-hidden px-6 pt-8 pb-6">
        <div className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-gradient-to-br from-[var(--tg-theme-button-color)]/25 to-purple-500/15 blur-3xl" />
        <div className="absolute -bottom-24 -left-16 w-52 h-52 rounded-full bg-gradient-to-tr from-cyan-400/15 to-[var(--tg-theme-button-color)]/10 blur-3xl" />

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-7">
            <div className="w-12 h-12 rounded-2xl bg-[var(--tg-theme-button-color)] flex items-center justify-center shadow-lg shadow-[var(--tg-theme-button-color)]/30">
              <span className="text-lg font-bold text-[var(--tg-theme-button-text-color)]">
                EP
              </span>
            </div>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Переключить тему"
              className="w-10 h-10 rounded-xl bg-[var(--tg-theme-section-bg-color)] flex items-center justify-center text-lg shadow-sm transition-all hover:opacity-80 active:scale-90"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>

          <div className="text-3xl mb-3">🎓</div>
          <h1 className="text-[22px] font-bold text-[var(--tg-theme-text-color)] leading-tight mb-2">
            Добро пожаловать,<br />{firstName}!
          </h1>
          <p className="text-sm text-[var(--tg-theme-hint-color)] leading-relaxed max-w-xs">
            EduPulse поможет вести учеников, расписание, посещаемость и платежи — в одном месте.
          </p>
        </div>
      </header>

      {/* ── Выбор формата работы ───────────────────────────────────────── */}
      <main className="flex-1 px-6 pb-6">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--tg-theme-hint-color)] mb-3">
          Как вы будете работать?
        </p>

        {optionCard(
          'solo',
          choice === 'solo',
          '🧑‍💻',
          'Я работаю один',
          'Сами ведёте учеников и занятия. Идеально для частных преподавателей и репетиторов.',
        )}

        {optionCard(
          'team',
          choice === 'team',
          '🏫',
          'У меня есть команда',
          'Создайте школу и пригласите учителей и администраторов для совместной работы.',
        )}

        {/* ── Школа: название ──────────────────────────────────────────── */}
        <div
          className={`grid transition-all duration-300 ease-in-out ${
            choice === 'team' ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="tg-card mt-1 mb-2">
              <label
                htmlFor="school-name"
                className="block text-xs font-medium text-[var(--tg-theme-hint-color)] mb-2"
              >
                Название школы
              </label>
              <input
                id="school-name"
                type="text"
                value={schoolName}
                onChange={e => {
                  setSchoolName(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="Например, «Школа робототехники»"
                maxLength={60}
                className="w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] border-2 border-transparent focus:border-[var(--tg-theme-button-color)] outline-none text-sm text-[var(--tg-theme-text-color)] placeholder-[var(--tg-theme-hint-color)]/60 transition-all"
              />
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] mt-2 leading-relaxed">
                Название увидят учителя и администраторы вашей школы. Его можно изменить позже.
              </p>
            </div>
          </div>
        </div>

        {/* ── Чекбокс согласия с условиями ──────────────────────────── */}
        <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={() => setTermsAccepted(!termsAccepted)}
            className="mt-0.5 w-4 h-4 rounded border-[var(--tg-theme-hint-color)]/40 text-[var(--tg-theme-button-color)] focus:ring-[var(--tg-theme-button-color)]/30 shrink-0"
          />
          <span className="text-[11px] text-[var(--tg-theme-hint-color)] leading-relaxed">
            Используя данное приложение, Вы принимаете наши{' '}
            <a href="/terms" target="_blank" className="underline hover:text-[var(--tg-theme-button-color)] transition-colors">Условия Использования</a>{' '}
            и{' '}
            <a href="/privacy" target="_blank" className="underline hover:text-[var(--tg-theme-button-color)] transition-colors">Политику Конфиденциальности</a>
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="mt-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-500 animate-fade-in"
          >
            {error}
          </div>
        )}
      </main>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <footer className="px-6 pb-8">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!choice || submitting || !termsAccepted}
          className={`w-full py-3.5 rounded-2xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
            !choice
              ? 'bg-[var(--tg-theme-button-color)]/30 text-[var(--tg-theme-button-text-color)]/50 cursor-not-allowed'
              : 'bg-gradient-to-r from-[var(--tg-theme-button-color)] to-purple-600 text-[var(--tg-theme-button-text-color)] shadow-lg shadow-[var(--tg-theme-button-color)]/25 hover:opacity-90 active:scale-[0.98]'
          }`}
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Сохранение...
            </>
          ) : choice === 'team' ? (
            <>🏫 Создать школу и продолжить</>
          ) : choice === 'solo' ? (
            <>🚀 Начать работу</>
          ) : (
            <>Выберите вариант выше</>
          )}
        </button>
        <p className="text-center text-[11px] text-[var(--tg-theme-hint-color)] mt-4">
          Позже вы сможете пригласить команду в настройках аккаунта
        </p>
      </footer>
    </div>
  );
}
